import { useState, useRef, useEffect, useCallback } from "react";

/**
 * Manages scroll arrow visibility and continuous scroll behavior.
 *
 * Single click: scrolls by `clickDistance` (fraction of container dimension, default 0.8).
 * Hold: after `holdDelay`ms, starts continuous scrolling.
 *
 * @param {any[]} deps - Extra dependencies that trigger a scroll state recheck
 * @returns {{ scrollViewportRef, canGoLeft, canGoRight, canGoUp, canGoDown, startContinuousScroll, stopContinuousScroll, getScrollHandlers }}
 */
const useScrollArrows = (deps = []) => {
  const scrollViewportRef = useRef(null);
  const scrollIntervalRef = useRef(null);
  const holdTimerRef = useRef(null);
  const isHoldingRef = useRef(false);

  const [canGoLeft, setCanGoLeft] = useState(false);
  const [canGoRight, setCanGoRight] = useState(false);
  const [canGoUp, setCanGoUp] = useState(false);
  const [canGoDown, setCanGoDown] = useState(false);

  const updateScrollState = useCallback(() => {
    const node = scrollViewportRef.current;
    if (!node) return;
    setCanGoLeft(node.scrollLeft > 0);
    setCanGoRight(node.scrollLeft + node.clientWidth < node.scrollWidth - 1);
    setCanGoUp(node.scrollTop > 0);
    setCanGoDown(node.scrollTop + node.clientHeight < node.scrollHeight - 1);
  }, []);

  useEffect(() => {
    const node = scrollViewportRef.current;
    if (!node) return;
    updateScrollState();
    node.addEventListener("scroll", updateScrollState);
    window.addEventListener("resize", updateScrollState);
    return () => {
      node.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateScrollState, ...deps]);

  const stopContinuousScroll = useCallback(() => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    isHoldingRef.current = false;
  }, []);

  /**
   * Starts continuous scrolling in a given direction.
   * Uses `behavior: "instant"` to avoid stacking smooth animations.
   *
   * @param {{ x?: number, y?: number }} direction
   * @param {number} scrollSpeed - Fraction of container dimension scrolled per second (default: 1.0)
   */
  const startContinuousScroll = useCallback((direction, scrollSpeed = 1.0) => {
    if (scrollIntervalRef.current) return;
    isHoldingRef.current = true;

    const INTERVAL_MS = 16; // ~60fps
    const TICKS_PER_SECOND = 1000 / INTERVAL_MS;

    scrollIntervalRef.current = setInterval(() => {
      const node = scrollViewportRef.current;
      if (!node) return;
      const dx = direction.x ?? 0;
      const dy = direction.y ?? 0;
      const scrollX = dx !== 0 ? (node.clientWidth * scrollSpeed) / TICKS_PER_SECOND * dx : 0;
      const scrollY = dy !== 0 ? (node.clientHeight * scrollSpeed) / TICKS_PER_SECOND * dy : 0;
      // instant (not smooth) so ticks don't queue up and fight each other
      node.scrollBy({ left: scrollX, top: scrollY, behavior: "instant" });
    }, INTERVAL_MS);
  }, []);

  /**
   * Returns { onPointerDown, onPointerUp, onPointerLeave } handlers to attach to a scroll arrow.
   *
   * On short press  → one-shot scroll by `clickDistance` (fraction of container, default 0.8).
   * On hold (≥ holdDelay ms) → continuous scroll until release.
   *
   * @param {{ x?: number, y?: number }} direction
   * @param {{ scrollSpeed?: number, clickDistance?: number, clickDistancePx?: number, holdDelay?: number }} options
   */
  const getScrollHandlers = useCallback((
    direction,
    { scrollSpeed = 1.0, clickDistance = 0.8, clickDistancePx = null, holdDelay = 150 } = {}
  ) => {
    const onPointerDown = (e) => {
      e.preventDefault();
      isHoldingRef.current = false;
      holdTimerRef.current = setTimeout(() => {
        startContinuousScroll(direction, scrollSpeed);
      }, holdDelay);
    };

    const onPointerUp = () => {
      const wasHolding = isHoldingRef.current;
      stopContinuousScroll();
      if (!wasHolding) {
        const node = scrollViewportRef.current;
        if (!node) return;
        const dx = direction.x ?? 0;
        const dy = direction.y ?? 0;
        // Prefer explicit px distance, fall back to fraction of container
        const scrollX = dx !== 0 ? dx * (clickDistancePx ?? node.clientWidth * clickDistance) : 0;
        const scrollY = dy !== 0 ? dy * (clickDistancePx ?? node.clientHeight * clickDistance) : 0;
        node.scrollBy({ left: scrollX, top: scrollY, behavior: "smooth" });
      }
    };

    return { onPointerDown, onPointerUp, onPointerLeave: stopContinuousScroll };
  }, [startContinuousScroll, stopContinuousScroll]);

  // Clear intervals on unmount
  useEffect(() => () => stopContinuousScroll(), []);

  return {
    scrollViewportRef,
    canGoLeft,
    canGoRight,
    canGoUp,
    canGoDown,
    startContinuousScroll,
    stopContinuousScroll,
    getScrollHandlers,
  };
};

export default useScrollArrows;